import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Stack, Title, Text, Button, Group, ThemeIcon } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';

interface SignInPromptModalProps {
  opened: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

/**
 * Light-weight sign-in prompt surfaced when a visitor tries to open a gated
 * course or the Knowledge Base. We let them keep their place — clicking
 * "Sign in" routes to the login page with the current URL as the redirect
 * target, so after auth they land back where they were.
 */
export function SignInPromptModal({
  opened,
  onClose,
  title = 'Sign in to continue',
  description = 'This course is available to signed-in viewers. It only takes a moment.',
}: SignInPromptModalProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignIn = () => {
    onClose();
    const redirect = encodeURIComponent(location.pathname + location.search);
    navigate(`/auth/login?redirect=${redirect}`);
  };

  return (
    <Modal opened={opened} onClose={onClose} centered withCloseButton radius="lg" size="md" title={undefined}>
      <Stack gap="md" align="center" ta="center" py="sm">
        <ThemeIcon size={56} radius="xl" variant="light" color="brand">
          <IconLock size={28} />
        </ThemeIcon>
        <div>
          <Title order={3}>{title}</Title>
          <Text c="dimmed" mt="xs">
            {description}
          </Text>
        </div>
        <Group mt="sm" w="100%" grow>
          <Button variant="subtle" onClick={onClose}>
            Not now
          </Button>
          <Button onClick={handleSignIn}>Sign in</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
